import { BaseButton } from '#src/base/button/base_button.tsx';
import { Input } from '#src/base/input/input.tsx';
import { cn } from '#src/styling/cn.ts';
import { WarningIcon } from '@phosphor-icons/react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

export type InputGroupProps = ComponentProps<'div'> & {
  errorMessage?: string;
};
export const InputGroup = ({ className, errorMessage, ...props }: InputGroupProps) => {
  const inputRow = (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        'group/input-group relative flex w-full items-center rounded-sm border border-gray-300 bg-transparent transition-colors',
        'hover:border-gray-400',

        // Focus state - when any input-group-control inside is focused
        'has-[[data-slot=input-group-control]:focus-visible]:border-violet-600',

        // Variants based on addon alignment
        'has-[>[data-align=inline-start]]:[&>input]:pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-2',

        // Error state
        'has-[[data-slot][aria-invalid=true]]:border-destructive',

        // Disabled state
        'has-[[data-slot=input-group-control]:disabled]:cursor-not-allowed has-[[data-slot=input-group-control]:disabled]:opacity-50',

        className,
      )}
      {...props}
    />
  );

  if (!errorMessage) return inputRow;

  return (
    <div className="flex flex-col gap-1">
      {inputRow}
      <div className="text-destructive flex items-center gap-1 text-xs">
        <WarningIcon className="size-3.5 shrink-0" />
        <span>{errorMessage}</span>
      </div>
    </div>
  );
};

const inputGroupAddonVariants = cva(
  'text-muted-foreground flex h-auto cursor-text items-center justify-center gap-2 text-sm font-medium select-none group-data-[disabled=true]/input-group:opacity-50',
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-2',
        'inline-end': 'order-last pr-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
);

export type InputGroupAddonProps = ComponentProps<'div'> &
  VariantProps<typeof inputGroupAddonVariants>;
export const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: InputGroupAddonProps) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={e => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva(
  'inline-flex items-center justify-center gap-1 text-sm font-medium shadow-none',
  {
    variants: {
      size: {
        xs: 'h-6 rounded-sm px-2',
        sm: 'h-8 gap-1.5 rounded-sm px-2.5',
        'icon-xs': 'size-6 rounded-sm',
        'icon-sm': 'size-8 rounded-sm',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  },
);

export type InputGroupButtonProps = Omit<ComponentProps<typeof BaseButton.Root>, 'size'> &
  VariantProps<typeof inputGroupButtonVariants>;
export const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  children,
  ...props
}: InputGroupButtonProps) => {
  return (
    <BaseButton.Root
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    >
      {children}
    </BaseButton.Root>
  );
};

export type InputGroupTextProps = ComponentProps<'span'>;
export const InputGroupText = ({ className, ...props }: InputGroupTextProps) => {
  return (
    <span
      className={cn(
        "text-muted-foreground flex items-center gap-2 text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
};

export type InputGroupInputProps = ComponentProps<'input'>;
export const InputGroupInput = ({ className, ...props }: InputGroupInputProps) => {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'h-11 flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:border-0 focus-visible:outline-hidden',
        className,
      )}
      {...props}
    />
  );
};
